import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Link2, AlertTriangle, Disc3, ArrowRight } from "lucide-react";
import api, { apiError, resolveImg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export function ImportLinkDialog({ open, onOpenChange, artistId, songId, onImported }) {
  const [url, setUrl] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const reset = () => { setUrl(""); setPreview(null); };

  const doPreview = async () => {
    if (!url.trim()) return toast.error("Paste a dashboard link");
    setLoading(true);
    setPreview(null);
    try {
      const { data } = await api.post("/import/preview", { url: url.trim() });
      setPreview(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const doImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/import", { url: url.trim(), artist_id: artistId || undefined, song_id: songId || undefined });
      const runs = data.campaigns_created || 1;
      const runNote = runs > 1 ? ` across ${runs} campaign runs` : "";
      toast.success(`Imported "${data.title}" — ${data.imported_posts} posts${runNote}`);
      onOpenChange(false);
      reset();
      onImported?.(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent data-testid="import-link-dialog" className="max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add dashboard link</DialogTitle>
          <DialogDescription>
            Paste a public campaign dashboard link. We'll rebuild it as a native Playbook campaign.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="flex items-center gap-1"><Link2 className="h-3 w-3" /> Dashboard URL</Label>
            <div className="mt-1 flex gap-2">
              <Input
                data-testid="input-import-url"
                value={url}
                onChange={(e) => { setUrl(e.target.value); setPreview(null); }}
                onKeyDown={(e) => e.key === "Enter" && doPreview()}
                placeholder="https://example.com/campaign-link"
              />
              <Button data-testid="preview-import-btn" onClick={doPreview} disabled={loading} variant="outline" className="shrink-0 rounded-full border-neutral-300">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Preview"}
              </Button>
            </div>
          </div>

          {preview && (
            <div data-testid="import-preview" className="space-y-3 rounded-2xl border border-[#E7DFCC] bg-[#FCF8EF] p-4">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-neutral-900">
                  {preview.cover_art ? <img src={resolveImg(preview.cover_art)} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-neutral-600"><Disc3 className="h-6 w-6" /></div>}
                </div>
                <div className="min-w-0">
                  <p className="truncate font-display text-lg font-bold">{preview.title}</p>
                  <p className="truncate text-sm text-neutral-500">{preview.artist} · {preview.provider}</p>
                  <p className="text-xs font-medium text-[#35606E]">
                    {preview.post_count} posts detected
                    {preview.campaign_run_count > 1 ? ` · ${preview.campaign_run_count} campaign runs` : ""}
                  </p>
                </div>
              </div>
              {preview.warnings?.length > 0 && (
                <div className="space-y-1.5">
                  {preview.warnings.map((w, i) => (
                    <p key={i} data-testid={`import-warning-${i}`} className="flex items-start gap-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {w}
                    </p>
                  ))}
                </div>
              )}
              <Button data-testid="confirm-import-btn" onClick={doImport} disabled={importing} className="group w-full rounded-full bg-[#35606E] py-6 hover:bg-[#294b57]">
                {importing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Import as Playbook campaign
                {!importing && <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
